import i18next from 'i18next';
import { BulkUploaderTemplateFile } from '../../components/upload/bulkUploader';
import { PlayerCreatePayload } from '../../state/reduxRepositories/players/player.interfaces';
import { isValidEmail } from '../../helpers/emailValidator';
import { di } from '../../state/inversify.config';
import { AddPlayerDomain } from './addPlayer.domain';
import { ConneqtOnAddPlayerDomain } from './addConneqtModal/conneqtOnAddPlayer.domain';
import { dateTime } from '../../time/datetime';
import { i18nConfig } from '../../state/i18nConfig';

const addPlayerDomain = di.get(AddPlayerDomain);
const conneqtOnAddPlayerDomain = di.get(ConneqtOnAddPlayerDomain);

const formatValidationLocalisedString = (
  i18nKey: string,
  colIdentifier: string,
  rowNum: number,
): string => {
  // @ts-ignore
  return `${i18next.t(i18nKey, {
    colIdentifier,
    rowNum,
  })}`;
};

const sanitizeString = (dirtyString: string): string => {
  if (!dirtyString || !dirtyString.length) {
    return '';
  }
  // TODO etc ...eg. .replace(/\t/g, '');
  return dirtyString.trim();
};

const validateName = (
  name: string,
  identifier: string,
  rowIndex: number,
): string[] => {
  const errors: Array<string> = [];
  if (!name) {
    errors.push(
      formatValidationLocalisedString(
        'bulkupload.BULK_UPLOAD_VALIDATION_MISSING_IDENTIFIER_ON_ROW',
        identifier,
        rowIndex + 1,
      ),
    );
    return errors;
  }

  // TODO - add other validations here
  // e.g. name contains invalid special characters, or emojis or other out of range UTF-8

  return errors;
};

export const validateEmail = (
  email: string,
  identifier: string,
  rowIndex: number,
): string[] => {
  const errors: Array<string> = [];
  if (!email) {
    errors.push(
      formatValidationLocalisedString(
        'bulkupload.BULK_UPLOAD_VALIDATION_MISSING_IDENTIFIER_ON_ROW',
        identifier,
        rowIndex + 1,
      ),
    );
    return errors;
  }

  if (!isValidEmail(email)) {
    errors.push(
      formatValidationLocalisedString(
        'bulkupload.BULK_UPLOAD_VALIDATION_INVALID_IDENTIFIER_ON_ROW',
        identifier,
        rowIndex + 1,
      ),
    );
  }
  // TODO - other validations

  return errors;
};

const validateDateOfBirth = (
  dateOfBirth: string,
  identifier: string,
  rowIndex: number,
): string[] => {
  const errors: Array<string> = [];
  if (!dateOfBirth) {
    errors.push(
      formatValidationLocalisedString(
        'bulkupload.BULK_UPLOAD_VALIDATION_MISSING_IDENTIFIER_ON_ROW',
        identifier,
        rowIndex + 1,
      ),
    );
    return errors;
  }

  const convertedDob = dateTime.convertToDate(
    sanitizeString(dateOfBirth),
    i18nConfig.bulkuploadFileTemplateDateFormatIsDMY,
  );

  if (
    convertedDob === null ||
    (convertedDob && !addPlayerDomain.isValidDOB(convertedDob))
  ) {
    errors.push(
      formatValidationLocalisedString(
        'bulkupload.BULK_UPLOAD_VALIDATION_INVALID_IDENTIFIER_ON_ROW',
        identifier,
        rowIndex + 1,
      ),
    );
  }

  // TODO - other validations

  return errors;
};

const COL_HEADER_FIRST_NAME: string = i18next.t(
  'bulkupload.BULK_UPLOAD_TEMPLATE_FILE_COL_HEADER_FIRST_NAME',
);
const COL_HEADER_LAST_NAME: string = i18next.t(
  'bulkupload.BULK_UPLOAD_TEMPLATE_FILE_COL_HEADER_LAST_NAME',
);
const COL_HEADER_DATE_OF_BIRTH: string = i18next.t(
  'bulkupload.BULK_UPLOAD_TEMPLATE_FILE_COL_HEADER_DATE_OF_BIRTH',
);
const COL_HEADER_GUARDIAN_EMAIL: string = i18next.t(
  'bulkupload.BULK_UPLOAD_TEMPLATE_FILE_COL_HEADER_GUARDIAN_EMAIL',
);
const COL_HEADER_FIRST_NAME_CELL_ADDRESS: string = 'A3';
const COL_HEADER_LAST_NAME_CELL_ADDRESS: string = 'B3';
const COL_HEADER_DATE_OF_BIRTH_CELL_ADDRESS: string = 'C3';
const COL_HEADER_GUARDIAN_EMAIL_CELL_ADDRESS: string = 'D3';

export const templateFile: BulkUploaderTemplateFile = {
  VERSION_NUMBER: i18nConfig.bulkuploadFileTemplateVersion,
  VERSION_NUMBER_CELL_ID: 'B2',
  HEADER_ROW_INDEX: 2,
  HEADER_ROW_CELLS: [
    {
      cellId: COL_HEADER_FIRST_NAME_CELL_ADDRESS,
      cellValue: COL_HEADER_FIRST_NAME,
    },
    {
      cellId: COL_HEADER_LAST_NAME_CELL_ADDRESS,
      cellValue: COL_HEADER_LAST_NAME,
    },
    {
      cellId: COL_HEADER_DATE_OF_BIRTH_CELL_ADDRESS,
      cellValue: COL_HEADER_DATE_OF_BIRTH,
    },
    {
      cellId: COL_HEADER_GUARDIAN_EMAIL_CELL_ADDRESS,
      cellValue: COL_HEADER_GUARDIAN_EMAIL,
    },
  ],
};

interface BulkUploadPlayerData extends Record<string, string | number> {
  rowIndex: number;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  guardianEmail: string;
}

const rowNum: string = '__rowNum__';

export const bulkUploadPlayerDataMapper = async (
  rawData: Record<string, string>[],
): Promise<BulkUploadPlayerData[]> => {
  return rawData.map((item) => ({
    // Convert the SheetJS internal __rowNum__ to a conventional/standard json property
    rowIndex: item[rowNum] as unknown as number,
    firstName: item[templateFile.HEADER_ROW_CELLS[0].cellValue],
    lastName: item[templateFile.HEADER_ROW_CELLS[1].cellValue],
    dateOfBirth: item[templateFile.HEADER_ROW_CELLS[2].cellValue],
    guardianEmail: item[templateFile.HEADER_ROW_CELLS[3].cellValue],
  }));
};

const validateRowItem = (item: BulkUploadPlayerData): string[] => {
  const { rowIndex } = item;

  return [
    ...validateName(
      sanitizeString(item.firstName),
      templateFile.HEADER_ROW_CELLS[0].cellValue,
      rowIndex,
    ),
    ...validateName(
      sanitizeString(item.lastName),
      templateFile.HEADER_ROW_CELLS[1].cellValue,
      rowIndex,
    ),
    ...validateDateOfBirth(
      sanitizeString(item.dateOfBirth),
      templateFile.HEADER_ROW_CELLS[2].cellValue,
      rowIndex,
    ),
    ...validateEmail(
      sanitizeString(item.guardianEmail),
      templateFile.HEADER_ROW_CELLS[3].cellValue,
      rowIndex,
    ),
  ];
};

export const bulkUploadPlayerValidator = async (
  data: BulkUploadPlayerData[],
): Promise<string[]> => {
  const messages: Array<string> = [];

  const deduplicatedAndValidatedData = data.filter((obj, index) => {
    const itemIndex = data.findIndex(
      (item) =>
        item.firstName === obj.firstName &&
        item.lastName === obj.lastName &&
        item.dateOfBirth === obj.dateOfBirth &&
        item.guardianEmail === obj.guardianEmail,
    );

    if (itemIndex === index) {
      // obj item is not duplicate, do validation
      messages.push(...validateRowItem(obj));
      return true;
    }

    messages.push(
      `${i18next.t('bulkupload.BULK_UPLOAD_VALIDATION_FOUND_DUPLICATE_AT_ROW', {
        rowNum: obj.rowIndex + 1,
      })}`,
    );

    return false;
  });

  if (data.length > deduplicatedAndValidatedData.length) {
    messages.push(
      `${i18next.t(
        'bulkupload.BULK_UPLOAD_VALIDATION_DUPLICATES_TO_BE_REMOVED',
        {
          numDuplicates: data.length - deduplicatedAndValidatedData.length,
        },
      )}`,
    );
  }

  return messages;
};

export const bulkUploadPlayerUploader = async (
  data: BulkUploadPlayerData[],
): Promise<string[]> => {
  const PLAYER_UPLOADER_SUCCESS: string = 'success';
  let numNewPlayers = 0;
  const messages: string[] = data.map((item: BulkUploadPlayerData) => {
    const firstName = sanitizeString(item.firstName);
    const lastName = sanitizeString(item.lastName);
    const converted = dateTime.convertToDate(
      sanitizeString(item.dateOfBirth),
      i18nConfig.bulkuploadFileTemplateDateFormatIsDMY,
    );
    const dateOfBirth =
      converted && dateTime.formatToUTCMidnightIsoDateTime(converted);
    const guardianEmail = sanitizeString(item.guardianEmail);

    if (!firstName || !lastName || !dateOfBirth || !guardianEmail) {
      return `${i18next.t(
        'bulkupload.BULK_UPLOAD_VALIDATION_COULD_NOT_PROCESS_ROW',
        {
          rowNum: `${item.rowIndex + 1}`,
        },
      )}`;
    }

    const playerCreatePayload: PlayerCreatePayload = {
      firstName,
      lastName,
      dateOfBirth,
    };

    if (!addPlayerDomain.isUnique(playerCreatePayload)) {
      return `${i18next.t(
        'bulkupload.BULK_UPLOAD_VALIDATION_DUPLICATE_PLAYER_ERROR',
        {
          duplicatePlayerName: `${playerCreatePayload.firstName} ${playerCreatePayload.lastName}`,
        },
      )}`;
    }

    if (
      !conneqtOnAddPlayerDomain.dispatchBulkUploadCreatePlayer(
        playerCreatePayload,
        sanitizeString(item.guardianEmail),
      )
    ) {
      return `${i18next.t(
        'bulkupload.BULK_UPLOAD_VALIDATION_ADD_NEW_PLAYER_ERROR',
        {
          playerFullName: `${playerCreatePayload.firstName} ${playerCreatePayload.lastName}`,
        },
      )}`;
    }

    numNewPlayers += 1;
    return PLAYER_UPLOADER_SUCCESS;
  });

  const filteredMessages: string[] = messages.filter(
    (message: string): boolean => message !== PLAYER_UPLOADER_SUCCESS,
  );
  return filteredMessages.concat(
    `${i18next.t('bulkupload.BULK_UPLOAD_VALIDATION_ADD_NEW_PLAYERS', {
      numNewPlayers,
    })}`,
  );
};
